import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';

import {
    assignQuickBooksProjectClass,
    assignQuickBooksProjectClasses,
    createQuickBooksProjectClass,
    fetchQuickBooksClassMap,
    markUnlinkedProjectsLegacy,
    syncQuickBooksProjects,
    type QuickBooksClass,
    type QuickBooksProjectClass,
} from './receiptApi';

const classIdsOn = (row: QuickBooksProjectClass): string[] =>
    row.qbo_class_ids?.length
        ? row.qbo_class_ids
        : row.qbo_class_id
          ? [row.qbo_class_id]
          : [];

const classNamesOn = (row: QuickBooksProjectClass): string[] =>
    row.qbo_class_names?.length
        ? row.qbo_class_names
        : row.qbo_class_name
          ? [row.qbo_class_name]
          : [];


export const QboClassesPage = () => {
    const [busy, setBusy] = useState(false);
    return (
        <Box py={3}>
            <QboClassMapPanel busy={busy} onBusy={setBusy} onRefresh={async () => {}} />
        </Box>
    );
};

export const QboClassMapPanel = ({
    busy,
    onBusy,
    onRefresh,
}: {
    busy: boolean;
    onBusy: (value: boolean) => void;
    onRefresh: () => Promise<void>;
}) => {
    const notify = useNotify();
    const [projects, setProjects] = useState<QuickBooksProjectClass[]>([]);
    const [classes, setClasses] = useState<QuickBooksClass[]>([]);
    const [filter, setFilter] = useState<'all' | 'linked' | 'unlinked' | 'legacy'>('all');
    const [editing, setEditing] = useState<number | null>(null);

    const load = async () => {
        const data = await fetchQuickBooksClassMap();
        setProjects(data.projects.filter(row => row.phase === 'active'));
        setClasses(data.classes);
    };

    useEffect(() => {
        void load().catch(error =>
            notify(error instanceof Error ? error.message : 'Could not load class map', {
                type: 'error',
            })
        );
    }, [notify]);

    const usedClassIds = useMemo(() => {
        const ids = new Set<string>();
        for (const row of projects) {
            for (const id of classIdsOn(row)) ids.add(id);
        }
        return ids;
    }, [projects]);
    const unusedClasses = useMemo(
        () => classes.filter(row => row.is_job && !usedClassIds.has(row.qbo_id)),
        [classes, usedClassIds]
    );
    const visible = useMemo(() => {
        if (filter === 'linked') return projects.filter(row => classIdsOn(row).length > 0);
        if (filter === 'unlinked') return projects.filter(row => classIdsOn(row).length === 0);
        if (filter === 'legacy') {
            return projects.filter(row => row.legacy_mrt_construction_qb);
        }
        return projects;
    }, [filter, projects]);
    const linked = projects.filter(row => classIdsOn(row).length > 0).length;
    const legacyCount = projects.filter(row => row.legacy_mrt_construction_qb).length;
    const unlinked = projects.filter(row => classIdsOn(row).length === 0).length;

    const pickClass = async (projectId: number, qboClassId: string | null) => {
        onBusy(true);
        try {
            await assignQuickBooksProjectClass(projectId, qboClassId);
            setEditing(null);
            await load();
            await onRefresh();
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Could not assign Class',
                { type: 'error' }
            );
        } finally {
            onBusy(false);
        }
    };

    const setProjectClasses = async (projectId: number, qboClassIds: string[]) => {
        onBusy(true);
        try {
            await assignQuickBooksProjectClasses(projectId, qboClassIds);
            setEditing(null);
            await load();
            await onRefresh();
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Could not assign Classes',
                { type: 'error' }
            );
        } finally {
            onBusy(false);
        }
    };

    const createClass = async (projectId: number, name?: string) => {
        onBusy(true);
        try {
            await createQuickBooksProjectClass(projectId, name);
            await load();
            await onRefresh();
            notify('Class created in MRT Roofing QuickBooks', { type: 'success' });
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Could not create Class',
                { type: 'error' }
            );
        } finally {
            onBusy(false);
        }
    };

    return (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
            <Typography variant="h6">MRT jobs ↔ QuickBooks Classes (MRT Roofing)</Typography>
            <Alert severity="info">
                Sync pulls Classes from MRT Roofing QuickBooks, then matches them to active jobs.
                One job ↔ one Class for normal work. Community jobs (e.g. Hammocks): attach every
                building Class to the same MRT project, then pick the building when posting a cost.
                Historic jobs from the old company show the chip{' '}
                <strong>Legacy MRT Construction QB</strong>.
            </Alert>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography color="text.secondary">
                    {linked} linked / {unlinked} unlinked / {legacyCount} legacy ·{' '}
                    {projects.length} active jobs · {unusedClasses.length} unused Classes
                </Typography>
                <TextField
                    select
                    size="small"
                    label="Show"
                    value={filter}
                    onChange={event => setFilter(event.target.value as typeof filter)}
                    sx={{ width: 160 }}
                >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="linked">Linked</MenuItem>
                    <MenuItem value="unlinked">Unlinked</MenuItem>
                    <MenuItem value="legacy">Legacy</MenuItem>
                </TextField>
                <Button
                    size="small"
                    variant="contained"
                    disabled={busy}
                    onClick={() =>
                        void (async () => {
                            onBusy(true);
                            try {
                                const counts = await syncQuickBooksProjects();
                                await load();
                                await onRefresh();
                                notify(
                                    `Matched ${counts.linked} · unchanged ${counts.unchanged} · unmatched ${counts.unmatched} · Classes ${counts.classes}`,
                                    { type: 'success' }
                                );
                            } catch (error) {
                                notify(
                                    error instanceof Error
                                        ? error.message
                                        : 'Match from QuickBooks failed',
                                    { type: 'error' }
                                );
                            } finally {
                                onBusy(false);
                            }
                        })()
                    }
                >
                    Match from QuickBooks
                </Button>
                <Button
                    size="small"
                    color="warning"
                    disabled={busy || unlinked === 0}
                    onClick={() => {
                        if (
                            !window.confirm(
                                `Mark all ${unlinked} unlinked active jobs as Legacy MRT Construction QB? New Roofing jobs you create later stay unlabeled. Run Match from QuickBooks first.`
                            )
                        ) {
                            return;
                        }
                        void (async () => {
                            onBusy(true);
                            try {
                                const result = await markUnlinkedProjectsLegacy();
                                await load();
                                await onRefresh();
                                notify(
                                    `Marked ${result.marked} jobs as Legacy MRT Construction QB`,
                                    { type: 'success' }
                                );
                            } catch (error) {
                                notify(
                                    error instanceof Error
                                        ? error.message
                                        : 'Could not mark legacy jobs',
                                    { type: 'error' }
                                );
                            } finally {
                                onBusy(false);
                            }
                        })();
                    }}
                >
                    Mark unlinked as Legacy
                </Button>
            </Stack>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>project_no</TableCell>
                        <TableCell>mrt_job_name</TableCell>
                        <TableCell>qbo_class_id</TableCell>
                        <TableCell>qbo_class_name</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {visible.map(row => (
                        <TableRow key={row.project_id}>
                            <TableCell>{row.project_no ?? '—'}</TableCell>
                            <TableCell>
                                <Stack spacing={0.5}>
                                    <Typography variant="body2">{row.name}</Typography>
                                    {row.legacy_mrt_construction_qb && (
                                        <Chip
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                            label="Legacy MRT Construction QB"
                                            sx={{ alignSelf: 'flex-start' }}
                                        />
                                    )}
                                </Stack>
                            </TableCell>
                            <TableCell>
                                {classIdsOn(row).length > 1
                                    ? `${classIdsOn(row).length} buildings`
                                    : row.qbo_class_id || '—'}
                            </TableCell>
                            <TableCell sx={{ minWidth: 320 }}>
                                {classIdsOn(row).length > 0 && editing !== row.project_id ? (
                                    <Stack spacing={1}>
                                        <Stack spacing={0.5}>
                                            {classNamesOn(row).map((name, index) => (
                                                <Stack
                                                    key={classIdsOn(row)[index] || name}
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                >
                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                        {name}
                                                    </Typography>
                                                    {classIdsOn(row).length > 1 && (
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            disabled={busy}
                                                            onClick={() => {
                                                                const next = classIdsOn(row).filter(
                                                                    id => id !== classIdsOn(row)[index]
                                                                );
                                                                void setProjectClasses(row.project_id, next);
                                                            }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                </Stack>
                                            ))}
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                size="small"
                                                disabled={busy || unusedClasses.length === 0}
                                                onClick={() => setEditing(row.project_id)}
                                            >
                                                Add building Class
                                            </Button>
                                            {classIdsOn(row).length === 1 && (
                                                <Button
                                                    size="small"
                                                    disabled={busy}
                                                    onClick={() => setEditing(row.project_id)}
                                                >
                                                    Change
                                                </Button>
                                            )}
                                            <Button
                                                size="small"
                                                color="error"
                                                disabled={busy}
                                                onClick={() => {
                                                    if (
                                                        window.confirm(
                                                            `Unlink all Classes from ${row.name}? Nothing changes in QuickBooks.`
                                                        )
                                                    ) {
                                                        void pickClass(row.project_id, null);
                                                    }
                                                }}
                                            >
                                                Unlink
                                            </Button>
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Autocomplete
                                            size="small"
                                            fullWidth
                                            disabled={busy || unusedClasses.length === 0}
                                            options={unusedClasses}
                                            value={null}
                                            getOptionLabel={qboClass => qboClass.name}
                                            isOptionEqualToValue={(option, value) =>
                                                option.qbo_id === value.qbo_id
                                            }
                                            filterOptions={(options, state) => {
                                                const query = state.inputValue.trim().toLowerCase();
                                                if (!query) return options;
                                                return options.filter(qboClass =>
                                                    qboClass.name.toLowerCase().includes(query)
                                                );
                                            }}
                                            onChange={(_, picked) => {
                                                if (!picked) return;
                                                const existing = classIdsOn(row);
                                                if (existing.length > 0) {
                                                    void setProjectClasses(row.project_id, [
                                                        ...existing,
                                                        picked.qbo_id,
                                                    ]);
                                                } else {
                                                    void pickClass(row.project_id, picked.qbo_id);
                                                }
                                            }}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    label="qbo_class_name"
                                                    placeholder="Search unused Class"
                                                />
                                            )}
                                        />
                                        {classIdsOn(row).length === 0 && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disabled={busy || !row.proposed_class_name}
                                                onClick={() =>
                                                    void createClass(
                                                        row.project_id,
                                                        row.proposed_class_name
                                                    )
                                                }
                                            >
                                                Create Class
                                            </Button>
                                        )}
                                        {editing === row.project_id && (
                                            <Button size="small" onClick={() => setEditing(null)}>
                                                Cancel
                                            </Button>
                                        )}
                                    </Stack>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Stack>
    );
};
